import React, { useState, useEffect } from 'react';
import anime from 'animejs';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { FaChevronDown, FaAnchor, FaCompass, FaWind, FaRegFilePdf, FaRegFileWord, FaShip, FaSignal, FaListUl, FaBroadcastTower  } from 'react-icons/fa';
import Typography from '@mui/material/Typography';

import { 
  visibilityTable, beaufortWindScale, figureSpellingData, 
  letterSpellingData, maritimeAcronymsData, morseCodeLettersAM, 
  morseCodeLettersNZ, morseCodeNumbers 
} from '../js/Data.js';

import docx from '../pdf/SP1REPORT.docx';
import docx2 from '../pdf/checklist.docx';
import pdf from '../pdf/SP-2REPORT.pdf';
import pdf2 from '../pdf/ScrubberUsageInTurkiye.pdf';
import pdf3 from '../pdf/DIRECTIVEWHICHISEFFECTIVEANDTOBEAPPLIEDATCANAKKALESTRAITASFROM01.02.2025.pdf';
import pdf4 from '../pdf/LOWSULPHURREGULATION.PDF';
import pdf5 from '../pdf/IMPLEMENTATIONDIRECTIVEFORTHEREGULATIONREGARDINGSEATRAFFICSCHEMEINTURKISHSTRAITS.pdf';

const renderTable = (data, headers) => (
  <div className="overflow-x-auto bg-[#fdfcfb] p-4 md:p-8">
    <table className="w-full text-left border-collapse min-w-[500px]">
      <thead>
        <tr className="border-b border-[#e5d5bc]">
          {headers.map((h) => (
            <th key={h} className="px-4 py-4 text-[10px] font-black uppercase tracking-[0.3em] text-[#9e8d71] whitespace-nowrap">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((row, i) => (
          <tr key={i} className="border-b border-[#f3eee7] hover:bg-[#f9f5f0] transition-colors">
            {headers.map((h) => (
              <td key={h} className="px-4 py-4 text-[14px] font-semibold text-[#4a4a4a] italic whitespace-nowrap">
                {row[h] || '—'}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function UsefulInformation() {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const tl = anime.timeline({
      easing: 'easeOutExpo',
    });

    tl
    .add({
      targets: '.header-reveal',
      opacity: [0, 1],
      translateY: [40, 0],
      duration: 1000,
    })
    .add({
      targets: '.doc-item',
      opacity: [0, 1],
      translateX: [-25, 0],
      delay: anime.stagger(80, { start: 200 }), 
      duration: 750
    }, '-=600')
    .add({
      targets: '.accordion-item',
      opacity: [0, 1],
      translateY: [25, 0],
      delay: anime.stagger(100, { start: 300 }), 
      duration: 750
    }, '-=650');
  }, []);

  const docs = [
    { name: 'SP1-Report', file: docx, type: 'word', code: 'DOC-01' },
    { name: ' Check List EK-3', file: docx2, type: 'word', code: 'DOC-02' },
    { name: 'SP-2 Report', file: pdf, type: 'pdf', code: 'PDF-03' },
    { name: 'Scrubber Usage In Turkiye', file: pdf2, type: 'pdf', code: 'PDF-04' },
    { name: 'Directive for Canakkale Strait', file: pdf3, type: 'pdf', code: 'PDF-05' },
    { name: 'Low Sulphur Regulation', file: pdf4, type: 'pdf', code: 'PDF-06' },
    { name: 'Implementation Directive for Sea Traffic', file: pdf5, type: 'pdf', code: 'PDF-07' },
  ];

  const accordionItems = [
    { title: 'Visibility Index', icon: <FaCompass />, data: visibilityTable, headers: ['Scale', 'Description', 'Limit'] },
    { title: 'Beaufort Wind Scale', icon: <FaWind />, data: beaufortWindScale, headers: ['Force', 'WindSpeed', 'WmoClassification', 'WaterAppearance', 'LandEffects'] },
    { title: 'Figure Spelling', icon: <FaListUl />, data: figureSpellingData, headers: ['Figure', 'Spelling', 'Pronounced'] },
    { title: 'Letter Spelling', icon: <FaAnchor />, data: letterSpellingData, headers: ['Letter', 'CodeWorld', 'Pronounced'] },
    { title: 'Maritime Acronyms', icon: <FaShip />, data: maritimeAcronymsData, headers: ['Acronym', 'FullForm'] },
    { title: 'Morse Code (A-M)', icon: <FaSignal />, data: morseCodeLettersAM, headers: ['Char', 'Code'] },
    { title: 'Morse Code (N-Z)', icon: <FaSignal />, data: morseCodeLettersNZ, headers: ['Char', 'Code'] },
    { title: 'Morse Code Numbers', icon: <FaSignal />, data: morseCodeNumbers, headers: ['Num', 'Code'] },
  ];

  return (
    <div className="bg-[#fdfcfb] min-h-screen font-sans selection:bg-[#c4a47c] selection:text-white">
      
      <section className="relative py-36 bg-[#0a111f] overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#c4a47c]/5 rounded-full blur-[100px] pointer-events-none"></div>
        <div className="absolute inset-0 opacity-5 flex items-center justify-center">
          <FaBroadcastTower size={200} className="text-white rotate-12" />
        </div>
        
        <div className="max-w-7xl mx-auto px-8 relative z-10 text-center header-reveal">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="h-[1px] w-12 bg-[#c4a47c]"></div>
            <FaAnchor className="text-[#c4a47c] text-sm" />
            <div className="h-[1px] w-12 bg-[#c4a47c]"></div>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white uppercase italic tracking-tighter leading-none mb-6">
            Useful <span className="text-[#c4a47c]">Information</span>
          </h1>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-8 -mt-16 relative z-20 pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          <div className="lg:col-span-4">
            <div className="bg-white p-10 rounded-[2.5rem] shadow-[0_40px_100px_rgba(0,0,0,0.05)] border border-[#f0e6d6]">
              <div className="flex items-center space-x-3 mb-10 border-b border-[#f0e6d6] pb-6">
                <FaShip className="text-[#c4a47c]" />
                <h3 className="text-lg font-black uppercase tracking-widest text-slate-900 leading-tight">Required Docs & <br/>Transit Regulations</h3>
              </div>
              
              <div className="space-y-5">
                {docs.map((doc, i) => (
                  <a key={i} href={doc.file} download className="doc-item flex items-center group transition-all duration-500">
                    <div className="w-10 h-10 flex items-center justify-center bg-slate-50 rounded-xl group-hover:bg-[#c4a47c] transition-all">
                      {doc.type === 'pdf' ? <FaRegFilePdf className="group-hover:text-white" /> : <FaRegFileWord className="group-hover:text-white" />}
                    </div>
                    <div className="ml-4 flex-1">
                      <p className="text-[14px] font-black text-slate-800 uppercase tracking-tight group-hover:text-[#c4a47c]">{doc.name}</p>
                      <p className="text-[9px] font-bold text-slate-300 uppercase tracking-widest">{doc.code}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-8 space-y-4">
            {accordionItems.map((item, idx) => (
              <div key={idx} className="accordion-item">
                <Accordion
                  expanded={expanded === idx}
                  onChange={() => setExpanded(expanded === idx ? false : idx)}
                  sx={{
                    background: 'white',
                    borderRadius: '2rem !important',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.02)',
                    border: '1px solid #f0e6d6',
                    overflow: 'hidden',
                    '&:before': { display: 'none' },
                    mb: 2,
                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': { borderColor: '#c4a47c', transform: 'translateY(-2px)' }
                  }}
                >
                  <AccordionSummary expandIcon={<FaChevronDown className="text-[#c4a47c] text-xs" />} sx={{ px: 4, py: 2 }}>
                    <div className="flex items-center space-x-4">
                      <div className="text-[#c4a47c] opacity-50">{item.icon}</div>
                      <Typography sx={{ fontWeight: 900, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.25em', color: '#0f172a' }}>
                        {item.title}
                      </Typography>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    {renderTable(item.data, item.headers)}
                  </AccordionDetails>
                </Accordion>
              </div>
            ))}
          </div>

        </div>
      </div>
      
      <div className="h-2 bg-gradient-to-r from-[#0a111f] via-[#c4a47c] to-[#0a111f]"></div>
    </div>
  );
}